import React, { useState, useEffect, useRef } from 'react';
import { extractLabResults, dataUrlToParts } from '../../services/labExtract';

// Lab-results entry for the labAnalysis (results-review) visit. ONE page: the
// patient can UPLOAD a PDF/photo of their lab report (auto-read into rows via
// /ai/extract-lab-results, then confirm/edit) OR type the rows manually — the
// upload control sits above the manual rows so both live together (no separate
// choose-method step). The parent sends the rows as `results[]` to create-visit-v2
// (Beluga /lab/receiveLabResults, PR #225/#230). Every row needs all 8 fields;
// Continue is gated on >=1 complete row so results[] is never empty. An uploaded
// file is handed to the parent (onLabDocUpload) to relay to the provider post-submit.

const BRAND = '#2d8a5f';

const STATUS_OPTIONS = [
  { value: '', label: 'Select…' },
  { value: 'N', label: 'Normal (in range)' },
  { value: 'H', label: 'High (above range)' },
  { value: 'L', label: 'Low (below range)' },
  { value: 'N/A', label: 'Not applicable' },
];

const SOURCE_OPTIONS = [
  { value: '', label: 'Select…' },
  { value: 'BLOOD', label: 'Blood' },
  { value: 'SERUM', label: 'Serum' },
  { value: 'PLASMA', label: 'Plasma' },
  { value: 'URINE', label: 'Urine' },
  { value: 'SALIVA', label: 'Saliva' },
];

// The 8 required fields per the BE contract. Order = display order.
const FIELDS = [
  { key: 'testName', label: 'Test name', type: 'text', placeholder: 'e.g., Testosterone, Total', col: 2 },
  { key: 'testResult', label: 'Result', type: 'text', placeholder: 'e.g., 248' },
  { key: 'testResultUnits', label: 'Units', type: 'text', placeholder: 'e.g., ng/dL (or N/A)' },
  { key: 'refRange', label: 'Reference range', type: 'text', placeholder: 'e.g., 300-1000 (or N/A)' },
  { key: 'statusIndicator', label: 'Flag', type: 'select', options: STATUS_OPTIONS },
  { key: 'sampleSource', label: 'Sample source', type: 'select', options: SOURCE_OPTIONS },
  { key: 'screeningDate', label: 'Collection date', type: 'text', placeholder: 'MM/DD/YYYY' },
  { key: 'reportDate', label: 'Report date', type: 'text', placeholder: 'MM/DD/YYYY' },
];

const ACCEPT = 'application/pdf,image/jpeg,image/png,image/heic,image/heif,image/webp';

const EMPTY_ROW = () => FIELDS.reduce((r, f) => ({ ...r, [f.key]: '' }), {});

export const isCompleteResultRow = (row) =>
  !!row && FIELDS.every((f) => typeof row[f.key] === 'string' && row[f.key].trim() !== '');

const inputCls =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2d8a5f]/40 focus:border-[#2d8a5f]';

const LabResultsEntryStep = ({ question, value, onAnswer, onNext, onBack, canGoBack, onLabDocUpload }) => {
  const [rows, setRows] = useState(
    Array.isArray(value) && value.length ? value : [EMPTY_ROW()],
  );
  const [extracting, setExtracting] = useState(false);
  const [uploadState, setUploadState] = useState('idle'); // idle | ok | empty
  const [uploadedName, setUploadedName] = useState('');
  const abortRef = useRef(null);

  // Keep the parent answer in sync so a Back/Forward round-trip preserves rows.
  useEffect(() => {
    onAnswer(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const setField = (idx, key, val) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  const addRow = () => setRows((prev) => [...prev, EMPTY_ROW()]);
  const removeRow = (idx) =>
    setRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const onFile = (file) => {
    if (!file) return;
    setUploadedName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const preview = reader.result; // data:<mime>;base64,<data>
      // Hand the file to the parent so the full document is relayed to the provider.
      onLabDocUpload?.({ fileName: file.name, fileType: file.type, preview });
      const parts = dataUrlToParts(preview, file.type);
      if (!parts) { setUploadState('empty'); return; }
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      setExtracting(true);
      extractLabResults(parts, abortRef.current.signal)
        .then((extracted) => {
          if (Array.isArray(extracted) && extracted.length) {
            // Normalize to full-field rows so editing is stable.
            setRows(extracted.map((e) => ({ ...EMPTY_ROW(), ...e })));
            setUploadState('ok');
          } else {
            setUploadState('empty');
          }
        })
        .catch(() => setUploadState('empty'))
        .finally(() => setExtracting(false));
    };
    reader.readAsDataURL(file);
  };

  const completeCount = rows.filter(isCompleteResultRow).length;
  const canContinue = completeCount >= 1 && !extracting;

  return (
    <div className="max-w-2xl mx-auto" data-testid="lab-results-entry">
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
        {question?.question || 'Your lab results'}
      </h2>
      <p className="text-gray-500 text-sm mt-2 mb-4">
        {question?.subtitle ||
          'Upload your lab report and we’ll fill this in for you to check — or enter your results manually. Every field is required.'}
      </p>

      {/* Upload control — same page as manual entry */}
      <div className="rounded-2xl border border-dashed border-[#2d8a5f]/50 bg-[#f0faf4] p-4 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <label
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 font-medium text-white cursor-pointer ${extracting ? 'opacity-60 pointer-events-none' : ''}`}
            style={{ backgroundColor: BRAND }}
            data-testid="lab-results-upload-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 16V4m0 0L8 8m4-4l4 4M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
            </svg>
            Upload a PDF or photo of your lab report
            <input type="file" accept={ACCEPT} className="hidden" data-testid="lab-results-upload-input"
              onChange={(e) => onFile(e.target.files && e.target.files[0])} />
          </label>
          <span className="text-xs text-gray-500">We’ll read it and fill in your results below for you to check.</span>
        </div>
        {extracting && (
          <div className="flex items-center gap-2 mt-3 text-sm text-gray-700" data-testid="lab-results-extracting">
            <span className="inline-block w-4 h-4 border-2 border-[#2d8a5f]/30 border-t-[#2d8a5f] rounded-full animate-spin" aria-hidden="true" />
            Reading {uploadedName || 'your lab report'}…
          </div>
        )}
        {!extracting && uploadState === 'ok' && (
          <p className="mt-3 text-sm text-gray-700" data-testid="lab-results-prefilled-notice">
            ✨ We filled these in from <strong>{uploadedName}</strong> — please check them and fix anything that looks off. Your full report is also sent to your provider.
          </p>
        )}
        {!extracting && uploadState === 'empty' && (
          <p className="mt-3 text-sm text-amber-800" data-testid="lab-results-noextract-notice">
            We couldn’t read <strong>{uploadedName}</strong> automatically — please enter your results below. Your uploaded report is still sent to your provider for review.
          </p>
        )}
      </div>

      <div className="space-y-4">
        {rows.map((row, idx) => {
          const complete = isCompleteResultRow(row);
          return (
            <div
              key={idx}
              data-testid={`lab-result-row-${idx}`}
              className={`rounded-2xl border p-4 ${complete ? 'border-[#2d8a5f]/40 bg-[#f0faf4]' : 'border-gray-200'}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Result {idx + 1}
                </span>
                {rows.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    data-testid={`remove-result-${idx}`}
                    className="text-xs text-gray-400 hover:text-red-600"
                  >
                    Remove
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {FIELDS.map((f) => (
                  <div key={f.key} className={f.col === 2 ? 'sm:col-span-2' : ''}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                    {f.type === 'select' ? (
                      <select
                        className={inputCls}
                        value={row[f.key]}
                        data-testid={`result-${idx}-${f.key}`}
                        onChange={(e) => setField(idx, f.key, e.target.value)}
                      >
                        {f.options.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        className={inputCls}
                        value={row[f.key]}
                        placeholder={f.placeholder}
                        data-testid={`result-${idx}-${f.key}`}
                        onChange={(e) => setField(idx, f.key, e.target.value)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={addRow}
        data-testid="add-result-row"
        className="mt-3 text-sm font-medium text-[#2d8a5f] hover:text-[#236b4a]"
      >
        + Add another result
      </button>

      {!canContinue && !extracting && (
        <p className="text-xs text-amber-700 mt-4" data-testid="lab-results-hint">
          Add at least one result with every field filled in to continue.
        </p>
      )}

      <div className="flex items-center gap-3 mt-6">
        {canGoBack && (
          <button
            type="button"
            onClick={onBack}
            className="rounded-xl border border-gray-300 px-5 py-3 font-medium text-gray-700 hover:bg-gray-50"
          >
            Back
          </button>
        )}
        <button
          type="button"
          onClick={onNext}
          disabled={!canContinue}
          data-testid="lab-results-continue"
          className="flex-1 rounded-xl px-5 py-3 font-semibold text-white transition-colors disabled:opacity-40"
          style={{ backgroundColor: BRAND }}
        >
          Continue{completeCount > 0 ? ` (${completeCount} result${completeCount > 1 ? 's' : ''})` : ''}
        </button>
      </div>
    </div>
  );
};

export { BRAND };
export default LabResultsEntryStep;
